import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import {
  type ColorValue,
  type StyleProp,
  TextInput,
  type TextInputInstance,
  View,
  type ViewStyle,
} from 'react-native';
import useSWR from 'swr';
import { useDebouncedCallback } from 'use-debounce';

import { Caption, Label, useLayout } from '~/common/styleguide';
import InputKeyHint from '~/components/InputKeyHint';
import { useQuickSearchState } from '~/context/QuickSearchState';
import {
  isSearchShortcutPressed,
  useSearchInputFocus,
  useSearchShortcut,
} from '~/hooks/useSearchInput';
import { type APIResponseType } from '~/types';
import tw from '~/util/tailwind';
import urlWithQuery from '~/util/urlWithQuery';

import { SearchIcon, SpinnerIcon } from './Icons';
import QuickSearchResult from './QuickSearchResult';

type Props = {
  focusOnMount?: boolean;
  style?: StyleProp<ViewStyle>;
};

const RESULTS_LIMIT = 5;

export default function QuickSearch({ focusOnMount = false, style }: Props) {
  const [activeResultIndex, setActiveResultIndex] = useState<number | null>(null);
  const { searchValue, setSearchValue } = useQuickSearchState();
  const [search, setSearch] = useState(searchValue);

  const inputRef = useRef<TextInputInstance>(null);
  const searchRef = useRef(searchValue);
  const isApple = useSearchShortcut(inputRef);
  const { isInputFocused, handleInputFocus, handleInputBlur } = useSearchInputFocus();

  const { push } = useRouter();
  const { isSmallScreen } = useLayout();

  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [focusOnMount]);

  const { data, isLoading } = useSWR<APIResponseType | undefined>(
    search.length ? `/api/libraries?search=${search}&limit=${RESULTS_LIMIT}` : undefined,
    (url: string) => fetch(url).then(res => res.json()),
    {
      revalidateOnFocus: false,
    }
  );
  const libraries = data?.libraries ?? [];
  const hasResults = Boolean(data?.total && data.total > 0);
  const shouldShowResults = search.length > 0 && isInputFocused;
  const effectiveActiveResultIndex =
    activeResultIndex !== null && activeResultIndex < libraries.length ? activeResultIndex : null;

  const typingCallback = useDebouncedCallback((text: string) => {
    setSearch(text);
  }, 200);

  function handleSearchChange(text: string) {
    searchRef.current = text;
    setSearchValue(text);
    setActiveResultIndex(null);
    typingCallback(text);
  }

  function handleSearchInputBlur() {
    handleInputBlur();
    setActiveResultIndex(null);
  }

  function handleResultHoverChange(index: number | null) {
    setActiveResultIndex(index);
  }

  function handleArrowDownPress() {
    setActiveResultIndex(currentIndex => {
      if (libraries.length === 0) {
        return null;
      }

      if (currentIndex === null) {
        return 0;
      }

      return Math.min(currentIndex + 1, libraries.length - 1);
    });
  }

  function handleArrowUpPress() {
    setActiveResultIndex(currentIndex => {
      if (libraries.length === 0) {
        return null;
      }

      if (currentIndex === null) {
        return libraries.length - 1;
      }

      return Math.max(currentIndex - 1, 0);
    });
  }

  async function handleResultSelect(npmPkg: string) {
    handleInputBlur();
    inputRef.current?.blur();
    setActiveResultIndex(null);
    await push(urlWithQuery(`/package/${npmPkg}`));
  }

  return (
    <View style={[tw`items-center bg-palette-gray6 py-3.5 dark:bg-dark`, style]}>
      <View style={tw`relative z-20 w-full max-w-layout px-5`}>
        <View style={tw`relative z-30 flex-row items-center`}>
          <View style={tw`pointer-events-none absolute left-4`}>
            {isLoading ? (
              <SpinnerIcon style={tw`-ml-0.5 text-primary`} />
            ) : (
              <SearchIcon style={isInputFocused ? tw`text-primary` : tw`text-white`} />
            )}
          </View>
          <TextInput
            ref={inputRef}
            id="quick-search"
            autoComplete="off"
            onKeyPress={event => {
              if (!('key' in event)) {
                return;
              }
              if (typeof event.key !== 'string') {
                return;
              }
              if (event.key === 'ArrowDown' && shouldShowResults && hasResults) {
                event.preventDefault();
                handleArrowDownPress();
              }
              if (event.key === 'ArrowUp' && shouldShowResults && hasResults) {
                event.preventDefault();
                handleArrowUpPress();
              }
              if (event.key === 'Enter') {
                event.preventDefault();

                if (effectiveActiveResultIndex !== null && libraries[effectiveActiveResultIndex]) {
                  void handleResultSelect(libraries[effectiveActiveResultIndex].npmPkg);
                  return;
                }

                void push(urlWithQuery('/packages', { search: searchRef.current }));
              }
              if (isSearchShortcutPressed(event)) {
                event.preventDefault();
              }
              if (inputRef.current && event.key === 'Escape') {
                if (activeResultIndex !== null) {
                  event.preventDefault();
                  setActiveResultIndex(null);
                  return;
                }

                if (search) {
                  event.preventDefault();
                  inputRef.current.clear();
                  searchRef.current = '';
                  setSearchValue('');
                  setSearch('');
                } else {
                  inputRef.current.blur();
                }
              }
            }}
            onFocus={handleInputFocus}
            onBlur={handleSearchInputBlur}
            onChangeText={handleSearchChange}
            value={searchValue}
            placeholder="Search libraries…"
            style={tw`h-12.5 font-sans pr-30 flex flex-1 rounded-md border-2 border-palette-gray5 bg-palette-gray6 p-4 pl-11 text-xl text-white -outline-offset-2 dark:border-default dark:bg-dark`}
            placeholderTextColor={tw`text-palette-gray4`.color as ColorValue}
          />
          {!isSmallScreen && (
            <View style={tw`pointer-events-none absolute right-4 flex-row items-center gap-1`}>
              {isInputFocused ? (
                <InputKeyHint
                  content={[
                    { label: 'press' },
                    { key: 'Enter' },
                    {
                      label:
                        activeResultIndex !== null
                          ? 'to open highlighted result'
                          : 'to see all results',
                    },
                  ]}
                />
              ) : (
                <InputKeyHint
                  content={[{ key: isApple ? 'Cmd' : 'Ctrl' }, { label: '+' }, { key: 'K' }]}
                />
              )}
            </View>
          )}
        </View>
        {shouldShowResults && (
          <View
            id="quick-search-results"
            style={tw`absolute inset-x-5 top-[46px] z-10 rounded-b border-2 border-t-0 border-pewter shadow-xl dark:border-default`}>
            {hasResults && (
              <View style={tw`pb-1.5 pt-2`}>
                {libraries.map((lib, index) => (
                  <QuickSearchResult
                    key={lib.npmPkg}
                    index={index}
                    library={lib}
                    isActive={effectiveActiveResultIndex === index}
                    onHoverChange={handleResultHoverChange}
                    onSelect={handleResultSelect}
                  />
                ))}
              </View>
            )}
            {hasResults && (data?.total ?? 0) > RESULTS_LIMIT && (
              <View
                style={[
                  tw`flex flex-row items-center gap-1.5 rounded-b border-t border-default bg-palette-gray1 px-3 py-1.5 dark:bg-dark`,
                  isSmallScreen ? tw`justify-center` : tw`justify-end`,
                ]}>
                <Label style={tw`font-light text-palette-gray4`}>
                  There are{' '}
                  <span style={tw`font-medium text-primary-darker dark:text-primary`}>
                    {(data?.total ?? 0) - RESULTS_LIMIT}
                  </span>{' '}
                  more results
                  {!isSmallScreen && (
                    <>
                      , press <span style={tw`font-medium`}>Enter</span> to see all of them
                    </>
                  )}
                </Label>
              </View>
            )}
            {!hasResults && (
              <Caption style={tw`pb-4.5 pt-5.5 text-center font-light text-secondary`}>
                There are no results for &quot;{search}&quot; query.
              </Caption>
            )}
          </View>
        )}
      </View>
    </View>
  );
}
